import { Building2, Leaf, LucideIcon, Server, Zap } from 'lucide-react';

export interface CategoryRecord {
  _id?: string;
  id?: string;
  categoryName?: string | { en?: string | null; tu?: string | null };
  name?: string;
  slugText?: string;
  isActive?: boolean;
}

export function getCategoryId(category: CategoryRecord | null | undefined): string {
  if (!category) return '';
  return category._id || category.id || '';
}

export function getCategoryDisplayName(
  category: CategoryRecord | string | null | undefined
): string {
  if (!category) return 'Uncategorized';
  if (typeof category === 'string') return category;

  if (typeof category.categoryName === 'string' && category.categoryName.trim()) {
    return category.categoryName;
  }

  if (
    category.categoryName &&
    typeof category.categoryName === 'object' &&
    category.categoryName.en
  ) {
    return category.categoryName.en;
  }

  if (category.name) return category.name;

  if (category.slugText) {
    return category.slugText
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return 'Uncategorized';
}

export function extractAssetCategory(
  categoryField: unknown,
  categoryNameById?: Map<string, string>
): { categoryId: string; categoryName: string } {
  if (!categoryField) {
    return { categoryId: '', categoryName: 'Uncategorized' };
  }

  if (Array.isArray(categoryField) && categoryField.length > 0) {
    const first = categoryField[0];
    if (typeof first === 'object' && first !== null) {
      const record = first as CategoryRecord;
      return {
        categoryId: getCategoryId(record),
        categoryName: getCategoryDisplayName(record),
      };
    }

    const categoryId = String(first);
    return {
      categoryId,
      categoryName: categoryNameById?.get(categoryId) || 'Uncategorized',
    };
  }

  if (typeof categoryField === 'object' && categoryField !== null) {
    const record = categoryField as CategoryRecord;
    return {
      categoryId: getCategoryId(record),
      categoryName: getCategoryDisplayName(record),
    };
  }

  const categoryId = String(categoryField);
  return {
    categoryId,
    categoryName: categoryNameById?.get(categoryId) || 'Uncategorized',
  };
}

export function getCategoryIcon(name: string): LucideIcon {
  const lower = name.toLowerCase();
  if (lower.includes('carbon')) return Leaf;
  if (lower.includes('renewable') || lower.includes('energy')) return Zap;
  if (lower.includes('data centre') || lower.includes('data center')) return Server;
  return Building2;
}

export function getCategoryColorClass(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('carbon')) return 'bg-success/20 text-success';
  if (lower.includes('renewable') || lower.includes('energy')) return 'bg-warning/20 text-warning';
  if (lower.includes('data centre') || lower.includes('data center')) {
    return 'bg-muted text-muted-foreground';
  }
  return 'bg-info/20 text-info';
}

export function resolveAssetTypeIdByCategoryName(
  categoryName: string,
  assetTypes: Array<{ _id: string; name: string }>
): string | undefined {
  if (!assetTypes.length) return undefined;

  const lower = categoryName.toLowerCase();
  let preferredName = 'Villa';

  if (lower.includes('renewable') || lower.includes('energy')) {
    preferredName = 'Hotel';
  } else if (
    lower.includes('data centre') ||
    lower.includes('data center') ||
    lower.includes('carbon') ||
    lower.includes('mining')
  ) {
    preferredName = 'Row House';
  }

  return (
    assetTypes.find((t) => t.name.toLowerCase() === preferredName.toLowerCase())?._id ??
    assetTypes[0]._id
  );
}
